import type { SxProps, Theme } from '@mui/material';
import { Box, IconButton, OutlinedInput, Stack, styled, useMediaQuery } from '@mui/material';
import Image from 'next/image';
import type { KeyboardEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidV4 } from 'uuid';

import { DisableSendIcon } from '@/src/shared/assets/DisableSendIcon';
import { EnableSendIcon } from '@/src/shared/assets/EnableSendIcon';
import { AnalyticsChartsData } from '@/src/shared/constants/AnalyticsConstants';
import { smallScreenTab } from '@/src/shared/constants/MediaQueryConstants';
import { useBoolean, useInputValue } from '@/src/shared/hooks';
import { useAppRoute } from '@/src/shared/hooks/useAppRoute';
import { chatApi } from '@/src/shared/services/apiServices/chatApis';
import type {
    AssistantMessage,
    Conversation,
    StreamChunk,
    UserMessage,
} from '@/src/shared/types/chat';
import { ChatApiAction, ChatMessageRole, StreamChunkContentType } from '@/src/shared/types/chat';
import type { RootState } from '@/src/store';
import {
    chatActions,
    selectChatMetadata,
    selectCurrentConversation,
    selectIsChatLoading,
    selectIsCurrentConversationNew,
} from '@/src/store/apps/chat';
import { labelColor, white } from '@/src/styles/theme/lightThemeOptions';
import { getBlobUrl } from '@/src/utils/commonUtil';
import { getCurrentISODate } from '@/src/utils/dateHelper';
import {
    getLatestInformativeMessageMap,
    getLatestTextChunkContent,
    parseStreamResponseByAxios,
} from '@/src/utils/streamResponseUtil';

import { UserSelectOptionsType } from '../../knowledge-base/ContentSource/constants';
import { ChatFlow } from './ChatFlow';
import { AdvancedFeatureList } from './ChatOptions';
import { NameBadge } from './NameBadge';

export const IntroMessageTypography = styled(Box)(({ theme }) => ({
    fontWeight: '400',
    fontSize: theme.spacing(3),
    lineHeight: theme.spacing(4),
    color: labelColor,
    maxWidth: '651px',
    textAlign: 'center',
    '@media(max-width: 767px)': {
        padding: theme.spacing(1.2, 0),
        maxWidth: '275px',
    },
}));

const InputStyle: SxProps<Theme> = (theme: Theme) => ({
    fontFamily: 'Equip_Regular',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    width: '100%',
    height: '100%',
    padding: theme.spacing(4, 4),
    borderRadius: '8px',
    backgroundColor: white,
    boxShadow: '0px 2px 8px 0px #00000026',
    '& fieldset': {
        borderWidth: 0,
    },
});

const StyledInputBox = styled(Box)<{ isCurrentConversationNew: boolean }>(
    ({ isCurrentConversationNew }) => ({
        flex: '0 0 auto',
        width: '80%',
        ...(isCurrentConversationNew && {
            marginBottom: '80px',
            maxWidth: '800px',
        }),
        height: '122px',
        position: 'relative',
        '& .MuiOutlinedInput-root': {
            display: 'flex',
            alignItems: 'flex-start',
            '& .MuiOutlinedInput-input': {
                maxHeight: '48px',
            },
        },
        '& .input-box-bottom-stack': {
            position: 'absolute',
            bottom: 0,
            width: '100%',
            height: '64px',
            '& .send-button': {
                backgroundColor: 'transparent',
                right: '13px',
                '@media(max-width: 767px)': {
                    right: '8px',
                },
                '& .MuiIconButton-root': {
                    padding: '8px',
                },
            },
        },
        '@media(max-width: 1024px)': {
            width: '95%',
            marginBottom: 0,
        },
    })
);

const InputField = ({
    conversation,
    isCurrentConversationNew,
}: {
    conversation: Conversation;
    isCurrentConversationNew: boolean;
}) => {
    const { t } = useTranslation('common');
    const { isPolli } = useAppRoute();
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const dispatch = useDispatch();
    const chatMetadata = useSelector(selectChatMetadata);
    const isChatLoading = useSelector(selectIsChatLoading);
    const [query, onChange, setQuery] = useInputValue('');
    const [isFocusing, { setTrue: startFocusing, setFalse: stopFocusing }] = useBoolean(false);
    const [selectedOption, setSelectedOption] = useState<UserSelectOptionsType>(
        UserSelectOptionsType.GEN_SEARCH
    );
    const handleSelectedOptionChange = (option: UserSelectOptionsType) => {
        setSelectedOption(option);
    };

    const pushMessageByChunks = useCallback((chunks: StreamChunk[], isStreaming?: boolean) => {
        const { activityId, conversationId, isAuthorize, role, responseStartTimestamp } =
            chunks[chunks.length - 1];
        const assistantMessage: AssistantMessage = {
            activityId,
            role,
            content: getLatestTextChunkContent(chunks),
            timestamp: responseStartTimestamp,
            informativeMessageMap: getLatestInformativeMessageMap(chunks),
            additionalCards: [],
            isStreaming,
            isAuthorize,
        };
        dispatch(
            chatActions.pushMessage({
                // use server returned conversationId
                conversationId,
                message: assistantMessage,
            })
        );
    }, []);

    const handleSend = useCallback(async () => {
        if (!query.trim()) {
            return;
        }

        dispatch(chatActions.setIsChatting(true));
        setQuery('');

        const { conversationId } = conversation;
        const queryMessage: UserMessage = {
            activityId: uuidV4(),
            role: ChatMessageRole.USER,
            content: query,
            timestamp: getCurrentISODate(),
        };
        dispatch(chatActions.pushMessage({ conversationId, message: queryMessage }));

        try {
            const isGeneralSearchSelected = selectedOption === UserSelectOptionsType.GEN_SEARCH;
            const isOrgSearchSelected = selectedOption === UserSelectOptionsType.ORG_SEARCH;

            const response = await chatApi({
                conversationId: isCurrentConversationNew ? '' : conversation.conversationId,
                activityId: queryMessage.activityId,
                query: isGeneralSearchSelected ? query : `#${selectedOption} ${query}`,
                userAction: isCurrentConversationNew ? ChatApiAction.NEW : ChatApiAction.CHAT,
                role: ChatMessageRole.USER,
                contents: [
                    {
                        content: isGeneralSearchSelected ? query : `#${selectedOption} ${query}`,
                        contentType: StreamChunkContentType.TEXT,
                    },
                ],
                userSelection: selectedOption,
                channel: !isPolli ? AnalyticsChartsData.web : AnalyticsChartsData.polli,
                ...((isGeneralSearchSelected || isOrgSearchSelected) && chatMetadata),
            });
            const onParse = async (_newlyParsedChunks: StreamChunk[], chunks: StreamChunk[]) => {
                pushMessageByChunks(chunks, true);
            };
            const finalChunks = await parseStreamResponseByAxios(response, onParse);
            pushMessageByChunks(finalChunks);
        } catch (error) {
            console.error(error);
            // TODO: ErrorBoundary need to refactor
            throw error;
        } finally {
            dispatch(chatActions.setIsChatting(false));
        }
    }, [query, conversation, isCurrentConversationNew, chatMetadata, selectedOption]);

    const onKeyDown = useCallback(
        (event: KeyboardEvent<HTMLTextAreaElement>) => {
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                handleSend();
            }
        },
        [handleSend]
    );

    useEffect(() => {
        if (!isChatLoading) {
            inputRef.current?.focus();
        }
    }, [isChatLoading, conversation]);

    return (
        <StyledInputBox
            className="input-container"
            isCurrentConversationNew={isCurrentConversationNew}
        >
            <OutlinedInput
                inputRef={inputRef}
                sx={InputStyle}
                placeholder={t('web_channel.input.placeholder') || ''}
                multiline
                maxRows={2}
                disabled={isChatLoading}
                value={query}
                onChange={onChange}
                onKeyDown={onKeyDown}
                onFocus={startFocusing}
                onBlur={stopFocusing}
            />
            <Stack
                className="input-box-bottom-stack"
                direction="row"
                justifyContent="space-between"
                alignItems="center"
            >
                <Stack className="chat-options" direction="row" justifyContent="space-between">
                    <AdvancedFeatureList
                        selectedOption={selectedOption}
                        handleSelectedOptionChange={handleSelectedOptionChange}
                    />
                </Stack>
                <IconButton
                    className={`send-button ${isFocusing ? 'focusing' : ''}`}
                    size="large"
                    onClick={handleSend}
                    edge="end"
                    disabled={isChatLoading || !query}
                >
                    {query ? <EnableSendIcon /> : <DisableSendIcon />}
                </IconButton>
            </Stack>
        </StyledInputBox>
    );
};

const ContainerStack = styled(Stack)<{ isCurrentConversationNew: boolean }>(
    ({ theme, isCurrentConversationNew }) => ({
        width: '100%',
        height: '100%',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(4),
        padding: theme.spacing(4, 0),
        ...(isCurrentConversationNew && {
            justifyContent: 'center',
            gap: theme.spacing(8),
            padding: 0,
        }),
    })
);

export const ChatContainer = () => {
    const { t } = useTranslation('common');
    const isCurrentConversationNew = useSelector(selectIsCurrentConversationNew);
    const currentConversation = useSelector(selectCurrentConversation);
    const user = useSelector((state: RootState) => state.user['state/user']);
    const welcomeScreenUrl = getBlobUrl('welcome');
    const isMobileScreen: boolean = useMediaQuery(smallScreenTab);

    return (
        <ContainerStack isCurrentConversationNew={isCurrentConversationNew}>
            {isCurrentConversationNew ? (
                <>
                    <NameBadge name={user?.givenName} />
                    <Image
                        src={welcomeScreenUrl}
                        alt="ask ginger"
                        className="ginger-chatbox-img"
                        priority
                        width={isMobileScreen ? 150 : 253}
                        height={isMobileScreen ? 120 : 196.88}
                    />
                    <IntroMessageTypography sx={{ marginTop: '-20px' }}>
                        {t('CHAT_WELCOME_PLACEHOLDER')}
                    </IntroMessageTypography>
                </>
            ) : (
                <ChatFlow conversation={currentConversation} />
            )}
            <InputField
                isCurrentConversationNew={isCurrentConversationNew}
                conversation={currentConversation}
            />
        </ContainerStack>
    );
};
